/*
 * ── src/lib/env.ts ────────────────────────────────────────────────────
 *
 * Shared reader for process.env values that come from operator-managed
 * dashboards (Vercel, GitHub Actions, .env files). Every one of those
 * inputs has produced at least one paste/pipe accident in this project:
 *
 *   • 2026-09-10 — UPSTASH_REDIS_REST_URL and UPSTASH_REDIS_REST_TOKEN
 *     were set with `printf 'value\n' | vercel env add`, which stored
 *     the literal newline. `new Redis({ url, token })` accepted the
 *     values, then every request produced a mystery `fetch failed`.
 *     Fixed with validateUpstashEnv() in rate-limit.ts.
 *
 *   • 2026-09-11 — NEXT_PUBLIC_GA_ID was set the same way; GA4
 *     accepted the trailing `%0A` in the collect URL but the
 *     property id embedded in every event was corrupted. Silent
 *     data-quality break.
 *
 *   • 2026-09-11 — RESEND_AUDIENCE_ID_INSIGHTS was set the same way;
 *     Resend's contacts endpoint rejects a trailing `%0A` in the
 *     path segment, so /api/insights/subscribe was silently dropping
 *     every subscription.
 *
 * The pattern is not going to stop. Trim and validate at the point
 * of read, and surface a specific error naming the variable and the
 * reason — never the value.
 *
 * See rate-limit.ts for the original validateUpstashEnv() design;
 * this module extracts its primitives so the same shape can be
 * reused everywhere a stray newline would fail silently.
 */

/** ASCII whitespace, tab, or LF/CR. Any of these inside a value that
 *  has already been `.trim()`-ed indicates a paste/pipe accident. */
const INTERIOR_WHITESPACE_RE = /\s/;

/** The LITERAL two-character sequence backslash + n (or r, t), which is
 *  what you see when a value was set with e.g.
 *  `printf 'x\n' | vercel env add` and the shell interpolated the
 *  escape as text rather than a control byte. */
const LITERAL_ESCAPE_RE = /\\[nrt]/;

export type EnvReadReason =
  | "missing"
  | "empty"
  | "whitespace"
  | "malformed";

export type EnvReadResult =
  | { ok: true; value: string }
  | { ok: false; reason: EnvReadReason; message: string };

export interface ReadEnvOptions {
  /** If true, an unset key is a hard error rather than `missing`. */
  required?: boolean;
  /** Optional predicate that runs after trim/whitespace checks.
   *  Return null on success or a short reason string on failure —
   *  the reason is embedded verbatim in the error message. Never
   *  return the value; keep secrets out of logs. */
  validate?: (trimmed: string) => string | null;
}

/**
 * Read `process.env[name]`, trim it, and reject the common
 * paste/pipe accidents.
 *
 * Note on client bundles: Next only inlines `NEXT_PUBLIC_*` reads
 * written as the LITERAL expression `process.env.NEXT_PUBLIC_FOO`.
 * A dynamic `process.env[name]` in code reachable from the browser
 * evaluates to undefined at runtime because Next strips the object.
 * For those sites use `readEnvValue(name, process.env.NEXT_PUBLIC_FOO, opts)`
 * so the literal read survives the compile and the value is validated
 * through the same pipeline.
 *
 * Does NOT log the value — errors name the variable and the reason,
 * never the contents.
 */
export function readEnv(name: string, opts: ReadEnvOptions = {}): EnvReadResult {
  return readEnvValue(name, process.env[name], opts);
}

/**
 * Same shape as `readEnv`, but the caller supplies the raw value.
 *
 * Use in client-reachable code so Next can inline the literal
 * `process.env.NEXT_PUBLIC_FOO` expression at build time. All
 * trim / whitespace / literal-escape / validator checks run
 * exactly as in `readEnv`.
 */
export function readEnvValue(
  name: string,
  raw: string | undefined,
  opts: ReadEnvOptions = {},
): EnvReadResult {
  if (raw === undefined) {
    return {
      ok: false,
      reason: "missing",
      message: `${name} is not set`,
    };
  }

  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return {
      ok: false,
      reason: "empty",
      message: `${name} is set but empty (only whitespace)`,
    };
  }

  if (INTERIOR_WHITESPACE_RE.test(trimmed) || LITERAL_ESCAPE_RE.test(trimmed)) {
    return {
      ok: false,
      reason: "whitespace",
      message:
        `${name} contains interior whitespace or a literal escape ` +
        `sequence (\\n, \\r, \\t). Check the value in the environment ` +
        `dashboard — it was likely set from a piped command that ` +
        `included a trailing newline.`,
    };
  }

  if (opts.validate) {
    const err = opts.validate(trimmed);
    if (err !== null) {
      return {
        ok: false,
        reason: "malformed",
        message: `${name} is malformed: ${err}`,
      };
    }
  }

  return { ok: true, value: trimmed };
}

/**
 * Read a trimmed env value or return `undefined` if the key is absent.
 *
 * Throws a clear Error naming the variable if the value is present
 * but corrupted (empty, whitespace, literal escape, or fails the
 * optional validate hook). Use for values whose absence is a
 * legitimate deploy mode (e.g. optional integrations).
 */
export function readEnvOptional(
  name: string,
  opts: Omit<ReadEnvOptions, "required"> = {},
): string | undefined {
  const r = readEnv(name, opts);
  if (r.ok) return r.value;
  if (r.reason === "missing") return undefined;
  throw new Error(r.message);
}

/**
 * Read a trimmed env value or throw with a specific message.
 *
 * Use for values whose absence is a hard configuration error
 * (e.g. Resend key, Supabase service role).
 */
export function readEnvRequired(
  name: string,
  opts: Omit<ReadEnvOptions, "required"> = {},
): string {
  const r = readEnv(name, opts);
  if (r.ok) return r.value;
  throw new Error(r.message);
}

/**
 * Read a trimmed env value, or return the fallback when the key is
 * absent OR empty. Corrupted values (interior whitespace, literal
 * escape) still throw — silently returning the fallback on a broken
 * value is exactly the behaviour this module exists to prevent.
 */
export function readEnvWithFallback(
  name: string,
  fallback: string,
  opts: Omit<ReadEnvOptions, "required"> = {},
): string {
  const r = readEnv(name, opts);
  if (r.ok) return r.value;
  if (r.reason === "missing" || r.reason === "empty") return fallback;
  throw new Error(r.message);
}

/**
 * Value-supplied variant of `readEnvWithFallback`, for client-reachable
 * code where the caller must write the LITERAL `process.env.NEXT_PUBLIC_*`
 * expression so Next inlines it at build time.
 *
 *   const MAP_TOKEN = readEnvValueWithFallback(
 *     "NEXT_PUBLIC_MAPBOX_TOKEN", process.env.NEXT_PUBLIC_MAPBOX_TOKEN, "",
 *   );
 */
export function readEnvValueWithFallback(
  name: string,
  raw: string | undefined,
  fallback: string,
  opts: Omit<ReadEnvOptions, "required"> = {},
): string {
  const r = readEnvValue(name, raw, opts);
  if (r.ok) return r.value;
  if (r.reason === "missing" || r.reason === "empty") return fallback;
  throw new Error(r.message);
}

/**
 * Validators intended for the `validate` option of readEnv().
 * Each returns null on success, or a short reason string on failure.
 * Reasons name the shape violation, not the value.
 */
export const envValidators = {
  httpsUrl: (v: string): string | null => {
    if (!v.startsWith("https://")) return "must start with https://";
    try {
      new URL(v);
      return null;
    } catch {
      return "not a valid URL";
    }
  },
  httpOrHttpsUrl: (v: string): string | null => {
    try {
      const u = new URL(v);
      if (u.protocol !== "https:" && u.protocol !== "http:") {
        return "must use http or https";
      }
      return null;
    } catch {
      return "not a valid URL";
    }
  },
  email: (v: string): string | null => {
    // Permissive shape check — Resend rejects everything else.
    // "Name <a@b.c>" and bare "a@b.c" are both accepted.
    const bare = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const withName = /^[^<>]+<\s*[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+\s*>$/;
    if (bare.test(v) || withName.test(v)) return null;
    return "not a valid email address";
  },
  gaMeasurementId: (v: string): string | null => {
    // GA4 measurement ids are G-XXXXXXXXXX. Reject anything else so
    // a copy-paste of a UA-… id or a random string surfaces clearly.
    if (!/^G-[A-Z0-9]{6,}$/.test(v)) {
      return "expected GA4 measurement id in the form G-XXXXXXXXXX";
    }
    return null;
  },
  uuid: (v: string): string | null => {
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v)) {
      return "expected a UUID";
    }
    return null;
  },
} as const;
